import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useLocation } from 'wouter';
import { Plus, ListMusic, Trash2, Calendar, Copy } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { api } from '../lib/api';

export function SetlistsPage() {
  const params = useParams<{ bandId: string }>();
  const bandId = Number(params.bandId);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const { data: band } = useQuery({
    queryKey: ['band', bandId],
    queryFn: () => api.bands.get(bandId),
  });

  const canManage = band?.role === 'owner' || band?.role === 'admin';
  const canDelete = band?.role === 'owner';

  const { data: setlists = [], isLoading } = useQuery({
    queryKey: ['setlists', bandId],
    queryFn: () => api.setlists.list(bandId),
  });

  const createSetlist = useMutation({
    mutationFn: api.setlists.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['setlists', bandId] });
      setShowCreate(false);
      setName('');
      setEventDate('');
    },
  });

  const deleteSetlist = useMutation({
    mutationFn: api.setlists.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['setlists', bandId] });
    },
  });

  const duplicateSetlist = useMutation({
    mutationFn: (id: number) => api.setlists.duplicate(id),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['setlists', bandId] });
      navigate(`/bands/${bandId}/setlists/${data.id}`);
    },
  });

  return (
    <Layout bandId={bandId} bandName={band?.name} title="Setlists">
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Setlists</h2>
          {canManage && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              New Setlist
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : setlists.length === 0 ? (
          <div className="text-center py-16">
            <ListMusic className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No setlists yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {setlists.map((sl: any) => (
              <div key={sl.id} className="bg-surface-900 border border-surface-700 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4">
                  <Link href={`/bands/${bandId}/setlists/${sl.id}`} className="flex-1">
                    <div className="cursor-pointer">
                      <h3 className="font-semibold hover:text-primary-300 transition-colors">{sl.name}</h3>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        {sl.eventDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(sl.eventDate).toLocaleDateString()}
                          </span>
                        )}
                        <span>{sl.songCount || 0} songs</span>
                      </div>
                    </div>
                  </Link>
                  {canManage && (
                    <button
                      onClick={(e) => { e.preventDefault(); duplicateSetlist.mutate(sl.id); }}
                      disabled={duplicateSetlist.isPending}
                      className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                      title="Duplicate setlist"
                    >
                      <Copy className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={(e) => { e.preventDefault(); if (confirm('Delete this setlist?')) deleteSetlist.mutate(sl.id); }}
                      className="p-2 hover:bg-red-900/30 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Modal open={showCreate} onClose={() => { setShowCreate(false); setName(''); setEventDate(''); }} title="New Setlist">
          <form onSubmit={e => { e.preventDefault(); createSetlist.mutate({ bandId, name, eventDate: eventDate || null }); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Setlist Name</label>
              <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="e.g. Friday Night Gig" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Event Date (optional)</label>
              <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <button type="submit" disabled={createSetlist.isPending} className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm">
              {createSetlist.isPending ? 'Creating...' : 'Create Setlist'}
            </button>
          </form>
        </Modal>
      </div>
    </Layout>
  );
}
